import React from 'react';
import { useIntl } from 'react-intl';
import { Slider } from '@aibuild/ui';
import type { UnderLightSettings } from '../viewport';
import { messages } from './messages';
import { UNDER_LIGHT_INTENSITY_MAX, UNDER_LIGHT_PRESETS, UNDER_LIGHT_TILT_MIN, hueOf, pastelAt } from './underLight';
import { Panel, Preset, Presets } from './UnderLightPanel.styled';

export interface UnderLightPanelProps {
  settings: UnderLightSettings;
  onChange: (settings: UnderLightSettings) => void;
}

const degrees = (value: number) => `${Math.round(value)}°`;

const UnderLightPanel = React.forwardRef<HTMLDivElement, UnderLightPanelProps>(function UnderLightPanel(
  { settings, onChange },
  ref,
) {
  const intl = useIntl();
  const label = intl.formatMessage(messages.underLightSettings);
  const update = (patch: Partial<UnderLightSettings>) => onChange({ ...settings, ...patch });
  return (
    <Panel ref={ref} role="dialog" aria-label={label} data-testid="model-viewer__under-light-panel">
      <Presets role="group" aria-label={intl.formatMessage(messages.underLightColour)}>
        {UNDER_LIGHT_PRESETS.map((color) => (
          <Preset
            key={color}
            type="button"
            aria-label={color}
            aria-pressed={settings.color === color}
            $color={color}
            $active={settings.color === color}
            onClick={() => update({ color })}
          />
        ))}
      </Presets>
      <Slider
        label={intl.formatMessage(messages.underLightColour)}
        value={hueOf(settings.color)}
        min={0}
        max={360}
        onChange={(hue) => update({ color: pastelAt(hue) })}
        formatValue={degrees}
        dataTestId="model-viewer__under-light-hue"
      />
      <Slider
        label={intl.formatMessage(messages.underLightIntensity)}
        value={settings.intensity}
        min={0}
        max={UNDER_LIGHT_INTENSITY_MAX}
        step={0.05}
        onChange={(intensity) => update({ intensity })}
        formatValue={(value) => value.toFixed(1)}
        dataTestId="model-viewer__under-light-intensity"
      />
      <Slider
        label={intl.formatMessage(messages.underLightAround)}
        value={settings.around}
        min={0}
        max={360}
        onChange={(around) => update({ around })}
        formatValue={degrees}
        dataTestId="model-viewer__under-light-around"
      />
      <Slider
        label={intl.formatMessage(messages.underLightTilt)}
        value={settings.tilt}
        min={UNDER_LIGHT_TILT_MIN}
        max={90}
        onChange={(tilt) => update({ tilt })}
        formatValue={degrees}
        dataTestId="model-viewer__under-light-tilt"
      />
    </Panel>
  );
});

export default UnderLightPanel;
